import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { PaymentReportAccess } from '../types/paymentReport';
import paymentReportService from '../services/paymentReportService';

interface ReportsAccessContextValue {
  access: PaymentReportAccess | null;
  loading: boolean;
  error: string | null;
  refreshAccess: () => Promise<void>;
}

const ReportsAccessContext = createContext<ReportsAccessContextValue | undefined>(undefined);

interface ReportsAccessProviderProps {
  children: ReactNode;
}

export const ReportsAccessProvider: React.FC<ReportsAccessProviderProps> = ({ children }) => {
  const [access, setAccess] = useState<PaymentReportAccess | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadAccess = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentReportService.getAccess();
      setAccess(response.data);
    } catch (err: any) {
      console.error('Failed to load reports access:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load reports access');
      setAccess(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  const value: ReportsAccessContextValue = {
    access,
    loading,
    error,
    refreshAccess: loadAccess,
  };

  return <ReportsAccessContext.Provider value={value}>{children}</ReportsAccessContext.Provider>;
};

export const useReportsAccess = (): ReportsAccessContextValue => {
  const context = useContext(ReportsAccessContext);
  if (context === undefined) {
    throw new Error('useReportsAccess must be used within a ReportsAccessProvider');
  }
  return context;
};
